// @flow

import maxBy from 'lodash/maxBy';
import { WithTranslation } from 'react-i18next';

import { currency } from 'lib/currency';
import { CurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { PromotionSummary } from 'lib/util/productShape';
import { getRelativeDateDescription } from 'lib/util/getRelativeDateDescription';

export const getPromotionTitleText = (
  promotion: PromotionSummary,
  t: WithTranslation['t'],
  locale: string,
  currencyFormatter: CurrencyFormatter
): string => {
  const percentDeltaPrice =
    promotion.prices &&
    promotion.prices.length > 0 &&
    promotion.prices.find((p) => Boolean(p.deltaPercent));

  let percentDeltaDescription = '';
  let fixedDeltaDescription = '';
  if (percentDeltaPrice) {
    percentDeltaDescription = percentDeltaPrice.deltaPercent;
  } else {
    if (!promotion.prices || promotion.prices.length === 0) {
      percentDeltaDescription = '0%';
    } else {
      const maxDeltaPrice = maxBy(promotion.prices, (price) => currency(price.deltaAmount).value);

      fixedDeltaDescription = currencyFormatter.format(
        (maxDeltaPrice && maxDeltaPrice.deltaAmount) || ''
      );
    }
  }

  switch (promotion.type) {
    case 'SIMPLE_DISCOUNT':
      if (percentDeltaDescription) {
        return t('Discount! {{discountDescription}} OFF!', {
          discountDescription: percentDeltaDescription,
        });
      } else {
        return t('Discount! Up to {{discountDescription}} OFF!', {
          discountDescription: fixedDeltaDescription,
        });
      }
    case 'ADDITIONAL_CHARGE':
      if (percentDeltaDescription) {
        return t('Additional Charge! Premium of +{{chargeDescription}} applies for this period!', {
          chargeDescription: percentDeltaDescription,
        });
      } else {
        return t(
          'Additional Charge! Premium of up to +{{chargeDescription}} applies for this period!',
          {
            chargeDescription: fixedDeltaDescription,
          }
        );
      }
    case 'EARLY_BIRD_DISCOUNT':
      if (!promotion.earlyBirdDeadline) {
        return '';
      }

      if (percentDeltaDescription) {
        return t(
          'Early Bird Discount! {{discountDescription}} OFF until {{relativeDateDescription}}!',
          {
            discountDescription: percentDeltaDescription,
            relativeDateDescription: getRelativeDateDescription(
              promotion.earlyBirdDeadline,
              t,
              locale
            ),
          }
        );
      } else {
        return t(
          'Early Bird Discount! Up to {{discountDescription}} OFF until {{relativeDateDescription}}!',
          {
            discountDescription: fixedDeltaDescription,
            relativeDateDescription: getRelativeDateDescription(
              promotion.earlyBirdDeadline,
              t,
              locale
            ),
          }
        );
      }
    case 'LAST_MINUTE_DISCOUNT':
      if (!promotion.lastMinuteStart) {
        return '';
      }

      if (percentDeltaDescription) {
        return t(
          'Last Minute Discount! {{discountDescription}} OFF starting {{relativeDateDescription}}!',
          {
            discountDescription: percentDeltaDescription,
            relativeDateDescription: getRelativeDateDescription(
              promotion.lastMinuteStart,
              t,
              locale
            ),
          }
        );
      } else {
        return t(
          'Last Minute Discount! Up to {{discountDescription}} OFF starting {{relativeDateDescription}}!',
          {
            discountDescription: fixedDeltaDescription,
            relativeDateDescription: getRelativeDateDescription(
              promotion.lastMinuteStart,
              t,
              locale
            ),
          }
        );
      }
    case 'GROUP_DISCOUNT':
      if (!promotion.groupDiscount) {
        return '';
      }

      if (percentDeltaDescription) {
        return t('Group Discount! {{discountDescription}} OFF for {{groupSize}} or more guests!', {
          discountDescription: percentDeltaDescription,
          groupSize: promotion.groupDiscount.min_guest_count,
        });
      } else {
        return t(
          'Group Discount! Up to {{discountDescription}} OFF for {{groupSize}} or more guests!',
          {
            discountDescription: fixedDeltaDescription,
            groupSize: promotion.groupDiscount.min_guest_count,
          }
        );
      }
    case 'FAMILY_DISCOUNT': {
      const familyDiscount = promotion.familyDiscount;
      if (!familyDiscount) {
        return '';
      }

      return t(
        'Family Discount! For every {{baseUnitCount}} x {{baseUnit}} booked, receive {{discountDescription}} off 1 x {{discountUnit}}!',
        {
          discountDescription: percentDeltaDescription || fixedDeltaDescription,
          baseUnitCount: familyDiscount.qualifying_base_guest_count,
          baseUnit: familyDiscount.base_guest_type_title || familyDiscount.base_guest_type_key,
          discountUnit:
            familyDiscount.discount_guest_type_title || familyDiscount.discount_guest_type_key,
        }
      );
    }
    default:
      return '';
  }
};
